import { useCallback, useState } from "react";

const ACCEPTED_MIME_SIGNATURES = [
  { mime: "image/jpeg", bytes: [0xff, 0xd8, 0xff] },
  { mime: "image/png", bytes: [0x89, 0x50, 0x4e, 0x47] },
  { mime: "image/gif", bytes: [0x47, 0x49, 0x46, 0x38] },
  // WebP: starts with "RIFF" (bytes 0-3), then "WEBP" at bytes 8-11
  {
    mime: "image/webp",
    bytes: [0x52, 0x49, 0x46, 0x46],
    extra: { offset: 8, bytes: [0x57, 0x45, 0x42, 0x50] },
  },
];

/**
 * Läser filens första bytes och jämför mot bildsignaturer (magic bytes).
 * Returnerar true om filen matchar ett accepterat bildformat.
 */
function checkMagicBytes(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const arr = new Uint8Array(reader.result);
      const isValid = ACCEPTED_MIME_SIGNATURES.some((sig) => {
        const headMatch = sig.bytes.every((byte, i) => arr[i] === byte);
        if (!headMatch) return false;
        if (sig.extra) {
          return sig.extra.bytes.every(
            (byte, i) => arr[sig.extra.offset + i] === byte,
          );
        }
        return true;
      });
      resolve(isValid);
    };
    reader.onerror = () => resolve(false);
    // 12 bytes för att identifiera format ovan (WebP behöver offset 8-11)
    reader.readAsArrayBuffer(file.slice(0, 12));
  });
}

export function useImageValidation() {
  const [validationErrors, setValidationErrors] = useState([]);

  const checkImageResolution = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const resolution = img.naturalWidth * img.naturalHeight;
        const maxResolution = 25 * 1_000_000; // 25 megapixels
        URL.revokeObjectURL(img.src);
        resolve({ file, rejected: resolution > maxResolution });
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error("Bildladdning misslyckades"));
      };
      img.src = URL.createObjectURL(file);
    });
  }, []);

  const validateFiles = useCallback(
    async (files) => {
      const rejectedImages = [];
      const maxFileSize = 10 * 1024 * 1024; // 10MB
      const errors = [];

      for (const file of files) {
        const fileErrors = [];
        let shouldReject = false;

        // Kontrollera filtyp via magic bytes
        const isValidImage = await checkMagicBytes(file);
        if (!isValidImage) {
          fileErrors.push(
            "Filformatet stöds inte (endast JPG, PNG, GIF, WebP)",
          );
          shouldReject = true;
        }

        // Kontrollera filstorlek
        if (file.size > maxFileSize) {
          fileErrors.push("För stor fil (max 10MB)");
          shouldReject = true;
        }

        // Kontrollera upplösning (bara om filen är en giltig bild)
        if (isValidImage) {
          try {
            const result = await checkImageResolution(file);
            if (result.rejected) {
              fileErrors.push("För hög upplösning (max 25MP)");
              shouldReject = true;
            }
          } catch (error) {
            console.warn("Bildvalidering misslyckades för:", file.name, error);
            fileErrors.push("Kunde inte läsas");
            shouldReject = true;
          }
        }

        // Lägg till fil i rejected och skapa sammanslaget felmeddelande
        if (shouldReject) {
          rejectedImages.push(file);
          errors.push(`${file.name}: ${fileErrors.join(" och ")}`);
        }
      }

      setValidationErrors(errors);
      return { rejectedImages, errors };
    },
    [checkImageResolution],
  );

  const clearValidationErrors = useCallback(() => {
    setValidationErrors([]);
  }, []);

  return {
    validateFiles,
    checkMagicBytes,
    checkImageResolution,
    validationErrors,
    clearValidationErrors,
  };
}
